import { InventoryBoundary } from '@cibo/core'
import {
  ACCEPTED_SHAPEFILES,
  MAX_SHAPEFILE_SIZE,
} from '@cibo/landmanager/src/features/UploadShapeFile/UploadShapefileTypes'
import {
  Alert,
  Button,
  CardActionArea,
  CardContent,
  CircularProgress,
  Collapse,
  Divider,
  Stack,
  Typography,
} from '@mui/material'
import { AxiosError } from 'axios'
import { useUploadBoundaryFile } from 'features/Inventories'
import { useCallback, useEffect, useState } from 'react'
import { FileRejection, FileWithPath, useDropzone } from 'react-dropzone'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { CreateBoundary } from './CreateBoundary'

export const UploadBoundaryFile = () => {
  const { t } = useTranslation('impact/MapLayerOverlay')
  const [, setParams] = useSearchParams()
  const { inventoryId } = useParams()
  const navigate = useNavigate()
  const [busy, setBusy] = useState(false)
  const [errorMessage, setErrorMessage] = useState<string | undefined>(undefined)
  const [acceptedFiles, setAcceptedFiles] = useState<FileWithPath[]>()
  const uploadBoundary = useUploadBoundaryFile({
    onSuccess: (item: InventoryBoundary) => {
      setBusy(false)
      setParams({ step: 'details', id: item.boundaryId })
    },
    onError: (error: AxiosError) => {
      setErrorMessage(error.response?.data.message || error.message)
      setBusy(false)
    },
  })

  useEffect(() => open(), [])

  const handleUpload = async (uploadFiles: FileWithPath[]) => {
    if (!uploadFiles?.length) {
      return
    }

    const formData = new FormData()
    if (uploadFiles.length > 1) {
      setErrorMessage(t('tooManyFiles'))
    }

    uploadFiles.forEach(file => {
      formData.append('file', file)
      formData.append('name', file.name)
    })

    setBusy(true)
    return uploadBoundary.mutateAsync(
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      { inventoryId: inventoryId!, formData }
    )
  }

  const onDrop = useCallback((acceptedFiles, rejections: FileRejection[]) => {
    setErrorMessage(undefined)
    setAcceptedFiles(acceptedFiles)

    if (acceptedFiles.length > 0) {
      setErrorMessage(undefined)
      handleUpload(acceptedFiles)
    } else {
      setErrorMessage(
        t('rejectedFile', {
          count: rejections.length,
          name: rejections.map(({ file: { name } }) => name).join(', '),
        })
      )
    }
  }, [])

  const { open, getInputProps } = useDropzone({
    onDrop,
    accept: ACCEPTED_SHAPEFILES,
    maxSize: MAX_SHAPEFILE_SIZE,
    multiple: false,
  })

  return (
    <>
      <input {...getInputProps()} />
      {busy && (
        <>
          <CardContent sx={{ padding: 3 }}>
            <Stack alignItems="center" spacing={2}>
              <CircularProgress />
              <Typography variant="body1" color="text.secondary">
                {acceptedFiles?.[0]?.name}
              </Typography>
            </Stack>
          </CardContent>
          <Divider />
          <CardActionArea
            sx={{ padding: 2, display: 'flex' }}
            style={{ pointerEvents: 'none' }}
            component="div"
          >
            <Button
              onClick={() => navigate(-1)}
              variant="text"
              color="secondary"
              style={{ pointerEvents: 'all' }}
            >
              {t('cancel')}
            </Button>
          </CardActionArea>
        </>
      )}
      {!busy && (
        <CreateBoundary onUploadShapefile={open} replace>
          <Collapse in={!!errorMessage}>
            <Alert severity="error">{errorMessage}</Alert>
          </Collapse>
        </CreateBoundary>
      )}
    </>
  )
}
